import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Trophy, TrendingUp, Crown, Medal, Award, ChevronRight } from 'lucide-react';
import { leaderboardAPI } from '../services/api';

const Leaderboards = () => {
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await leaderboardAPI.get({ period, limit: 50 });
      setTraders(res.data || []);
    } catch {
      setTraders([]);
    }
    setLoading(false);
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-[#F59E0B]" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-[#94A3B8]" />;
    if (rank === 3) return <Award className="w-5 h-5 text-[#B45309]" />;
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0A0E17]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#F59E0B]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-[#F59E0B]" />
          </div>
          <h1 className="font-heading text-[#F8FAFC] text-4xl sm:text-5xl font-medium tracking-tight mb-2" data-testid="leaderboards-title">Leaderboards</h1>
          <p className="text-[#94A3B8] text-lg">Top traders on Bollywood Sensex</p>
        </div>

        <div className="flex justify-center gap-2 mb-8">
          {[{k:'week',l:'This Week'},{k:'month',l:'This Month'},{k:'all',l:'All Time'}].map(({k,l}) => (
            <Button key={k} variant={period === k ? 'default' : 'outline'} onClick={() => setPeriod(k)}
              className={period === k ? 'bg-[#F59E0B] hover:bg-[#D97706] text-black' : 'border-[#1E293B] text-[#94A3B8] hover:text-white hover:border-[#64748B]'}
              data-testid={`period-${k}-btn`}
            >{l}</Button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-[#121826] rounded-2xl border border-[#1E293B] animate-pulse" />)}
          </div>
        ) : (
          <>
            {traders.length >= 3 && (
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {traders.slice(0, 3).map((t, i) => (
                  <Link to={`/profile/${t.username}`} key={t.user_id}
                    className={`rounded-2xl p-6 text-center relative overflow-hidden border transition-all duration-300 hover:shadow-[0_0_20px_rgba(245,158,11,0.1)] ${
                      i === 0 ? 'bg-[#F59E0B]/10 border-[#F59E0B]/30 md:scale-105 md:z-10' :
                      i === 1 ? 'bg-[#94A3B8]/10 border-[#94A3B8]/20' :
                      'bg-[#B45309]/10 border-[#B45309]/20'
                    }`} data-testid={`top-trader-${i+1}`}
                  >
                    <div className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                      i === 0 ? 'bg-[#F59E0B] text-black' : i === 1 ? 'bg-[#94A3B8] text-black' : 'bg-[#B45309] text-white'
                    }`}>{i + 1}</div>
                    <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold font-heading ${
                      i === 0 ? 'bg-[#F59E0B] text-black' : i === 1 ? 'bg-[#94A3B8] text-black' : 'bg-[#B45309] text-white'
                    }`}>{t.username?.slice(0, 2).toUpperCase()}</div>
                    <h3 className="text-[#F8FAFC] font-bold text-xl mb-1 font-heading">{t.username}</h3>
                    <p className="text-[#64748B] text-sm mb-4">Rank #{i + 1}</p>
                    <div className="text-2xl font-bold text-[#F8FAFC] mb-2 font-mono">B₹{((t.net_worth || 0) / 100000).toFixed(1)}L</div>
                    <div className={`flex items-center justify-center gap-1 font-mono font-medium ${(t.change_percent || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                      <TrendingUp className="w-4 h-4" />
                      {(t.change_percent || 0) >= 0 ? '+' : ''}{(t.change_percent || 0).toFixed(1)}%
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <div className="bg-[#121826] rounded-2xl border border-[#1E293B] overflow-hidden">
              <div className="bg-[#1E293B] px-6 py-4">
                <h2 className="text-[#F8FAFC] font-bold text-lg font-heading">Full Rankings</h2>
              </div>
              <div className="divide-y divide-[#1E293B]">
                {traders.map((t, i) => (
                  <Link key={t.user_id} to={`/profile/${t.username}`} className="flex items-center gap-4 px-6 py-4 hover:bg-[#1E293B]/50 transition-colors" data-testid={`leaderboard-row-${i}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm font-mono ${
                      i === 0 ? 'bg-[#F59E0B] text-black' : i === 1 ? 'bg-[#94A3B8] text-black' : i === 2 ? 'bg-[#B45309] text-white' : 'bg-[#1E293B] text-[#64748B]'
                    }`}>{t.rank || i + 1}</div>
                    <div className="w-8">{getRankBadge(i + 1)}</div>
                    <div className="w-10 h-10 rounded-full bg-[#1E293B] flex items-center justify-center">
                      <span className="text-[#F59E0B] font-bold text-sm">{t.username?.slice(0, 2).toUpperCase()}</span>
                    </div>
                    <div className="flex-1">
                      <div className="text-[#F8FAFC] font-semibold">{t.username}</div>
                      <div className="text-[#64748B] text-sm">{t.total_trades || 0} trades</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[#F8FAFC] font-bold font-mono">B₹{((t.net_worth || 0) / 100000).toFixed(1)}L</div>
                      <div className={`text-sm font-mono flex items-center justify-end gap-1 ${(t.change_percent || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                        <TrendingUp className="w-3 h-3" />
                        {(t.change_percent || 0) >= 0 ? '+' : ''}{(t.change_percent || 0).toFixed(1)}%
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#64748B]" />
                  </Link>
                ))}
              </div>
            </div>

            {traders.length === 0 && (
              <div className="text-center py-16">
                <Trophy className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                <p className="text-[#94A3B8]">No traders yet. Be the first to trade!</p>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Leaderboards;
